import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell, ChevronRight, Menu, Target } from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import { useAuth } from "@/context/auth-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { goalsApi, type Goal, type GoalMessage } from "@/api/goals";
import { queryKeys } from "@/lib/query-keys";
import { MobileAvatar } from "@/components/mobile/mobile-avatar";
import { MobileScreenHeader } from "@/components/mobile/mobile-screen-header";
import { AgentStatusPill } from "@/components/mobile/agent-status-pill";
import { useMobileShell } from "@/components/layout/mobile-channel-context";

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function todayLabel(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export function MobileHome() {
  const { tenantId, tenantName } = useTenant();
  const shell = useMobileShell();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: agents = [] } = useAgentsByCompany(tenantId);

  const { data: goals = [] } = useQuery({
    queryKey: queryKeys.goals.byCompany(tenantId),
    queryFn: () => goalsApi.list(tenantId),
  });

  const ownedGoalIds = useMemo(
    () => goals.filter((g: Goal) => g.ownerId).map((g: Goal) => g.id),
    [goals],
  );

  const { data: goalMessages = [] } = useQuery({
    queryKey: ["goal-messages-inbox", tenantId],
    queryFn: async () => {
      const msgs: GoalMessage[] = [];
      for (const gid of ownedGoalIds) {
        msgs.push(...(await goalsApi.listMessages(gid)));
      }
      return msgs;
    },
    enabled: ownedGoalIds.length > 0,
  });

  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const needsYou = useMemo(
    () =>
      goalMessages
        .filter(
          (m) =>
            m.direction === "agent_to_human" &&
            (m.messageType === "question" || m.messageType === "blocker") &&
            (!m.readAt || m.createdAt > oneDayAgo),
        )
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 2),
    [goalMessages, oneDayAgo],
  );

  const firstName = user?.name?.split(/\s+/)[0] ?? "there";
  const activeCount = agents.filter((a) => a.status === "active" || a.status === "running").length;
  const totalAgents = agents.length;
  const needsCount = goalMessages.filter(
    (m) => m.direction === "agent_to_human" && !m.readAt,
  ).length;

  const getAgent = (id: string | null) => agents.find((a) => a.id === id);
  const getGoalTitle = (id: string) => goals.find((g: Goal) => g.id === id)?.title || "Goal";

  return (
    <div className="flex h-full flex-col">
      <MobileScreenHeader
        title={tenantName || "Home"}
        subtitle={todayLabel()}
        leftIcon={<Menu size={20} />}
        onLeftClick={() => shell?.openDrawer?.()}
        rightIcon={<Bell size={20} />}
        onRightClick={() => navigate(`/tenants/${tenantId}/notifications`)}
        tall
      />

      <div
        className="min-h-0 flex-1 overflow-auto px-4 pt-4"
        style={{ paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))" }}
      >
        {/* Greeting / hero card */}
        <button
          onClick={() => navigate(`/tenants/${tenantId}/inbox`)}
          className="mg-accent-fill mb-5 block w-full rounded-[28px] p-[18px] text-left"
        >
          <div className="text-[13px] opacity-85">
            {greeting()}, {firstName}
          </div>
          <div className="mt-1.5 text-[18px] font-semibold leading-snug tracking-tight">
            {totalAgents === 0
              ? "No agents deployed yet. Add one to get started."
              : `Your ${totalAgents} agent${totalAgents === 1 ? "" : "s"} handled tasks overnight.${
                  needsCount > 0 ? ` ${needsCount} item${needsCount === 1 ? "" : "s"} need your decision.` : ""
                }`}
          </div>
          <span
            className="mt-3.5 inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold"
            style={{ background: "rgba(255,255,255,0.22)" }}
          >
            Review items
            <ChevronRight size={14} />
          </span>
        </button>

        {/* Your agents */}
        <div className="mb-2.5 flex items-center justify-between">
          <div className="text-[17px] font-semibold tracking-tight text-text-primary">Your agents</div>
          <button
            onClick={() => navigate(`/tenants/${tenantId}/agents`)}
            className="text-[13px] font-medium text-accent"
          >
            See all
          </button>
        </div>
        <div className="mb-5 grid grid-cols-2 gap-2.5">
          {agents.slice(0, 4).map((a) => (
            <button
              key={a.id}
              onClick={() => navigate(`/tenants/${tenantId}/workspace?agentId=${a.id}`)}
              className="glass-card rounded-[28px] p-3.5 text-left"
            >
              <div className="mb-2.5 flex items-center gap-2">
                <MobileAvatar name={a.name} avatarUrl={a.avatarUrl} size={36} status={a.status} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold leading-tight text-text-primary">
                    {a.name}
                  </div>
                  <AgentStatusPill status={a.status} mini />
                </div>
              </div>
              <div className="text-[11px] text-text-muted">
                {a.status === "running"
                  ? "Running…"
                  : a.status === "paused"
                    ? "Waiting for approval"
                    : a.status === "idle"
                      ? "Idle"
                      : a.title || "Active"}
              </div>
            </button>
          ))}
          {agents.length === 0 && (
            <div className="glass-card col-span-2 rounded-[28px] p-5 text-center text-[13px] text-text-muted">
              No agents yet. <button onClick={() => navigate(`/tenants/${tenantId}/agents/new`)} className="font-medium text-accent">Create one</button>.
            </div>
          )}
        </div>

        {/* Needs you */}
        <div className="mb-2.5 flex items-center justify-between">
          <div className="text-[17px] font-semibold tracking-tight text-text-primary">Needs you</div>
          {totalAgents > 0 && (
            <span className="text-[11px] text-text-muted">
              {activeCount} active · {totalAgents} total
            </span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {needsYou.length === 0 ? (
            <div className="glass-card rounded-[28px] p-4 text-[13px] text-text-muted">
              All clear. Nothing needs your attention.
            </div>
          ) : (
            needsYou.map((m) => {
              const agent = getAgent(m.agentId);
              const tone = m.messageType === "blocker" ? "danger" : "accent";
              return (
                <button
                  key={m.id}
                  onClick={() => navigate(`/tenants/${tenantId}/inbox`)}
                  className="glass-card flex gap-2.5 rounded-[28px] p-3.5 text-left"
                >
                  <MobileAvatar
                    name={agent?.name || "Agent"}
                    avatarUrl={agent?.avatarUrl}
                    size={32}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-[13px] font-semibold text-text-primary">
                        {agent?.name || "Agent"}
                      </span>
                      <span className="text-[11px] text-text-muted">
                        · {relativeTime(m.createdAt)}
                      </span>
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-text-secondary">
                      {m.content}
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          tone === "danger" ? "bg-danger/10 text-danger" : "bg-accent/10 text-accent"
                        }`}
                      >
                        {m.messageType}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-text-muted">
                        <Target size={11} /> {getGoalTitle(m.goalId)}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
